import React, { useCallback, useRef, useState } from 'react';
import {
  FavoritesModal,
  Heading,
  Icons,
  IconSpinner,
  SecondaryButton,
} from '../../../../components';
import {
  formatStringtoCamelCase,
  getFirstListingImageUrl,
  isArrayLength,
} from '../../../../util/genericHelpers';
import { FormattedMessage } from 'react-intl';
import {
  getDisplayName,
  getSubscriptionForListing,
  getUserSubscription,
  getUserSubscriptionLookupkeys,
  getUserType,
  retrieveUserFavoritesList,
} from '../../../../util/userDataExtraction';
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../util/routes';
import {
  brandIndustriesOptions,
  EditListingParamsType,
  EditListingTab,
  SectionBrandHeroTabs,
  SUBSCRIPTION_TYPES,
  SubscriptionStatus,
  UserType,
} from '../../../../util/enums';
import { useListingPageContext } from '../../context/ListingPageContext';
import BrandWhiteLabelBanner from './BrandWhiteLabelBanner';
import BrandsInlineBanner from './BrandsInlineBanner';
import RenderCampaigns from '../../SharedComponents/RenderCampaigns';
import RenderJobPost from '../../SharedComponents/RenderJobPost';
import RenderPerks from '../../SharedComponents/RenderPerks';
import RenderCouponOffer from '../../SharedComponents/RenderCouponOffer';

import css from './SectionBrandHero.module.css';
import BrandCustomLinks from './BrandCustomLinks';
import CampaignShowCase from './CampaignShowCase';
import SocialMediaFeeds from './SocialMediaFeeds';
import PostedContent from './PostedContent';
import { selectIsAuthenticated } from '../../../../ducks/auth.duck';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../../../ducks/user.duck';
import RenderUserSubscriptionMaybe from '../../../EditListingPage/EditListingWizard/EditListingTalentPlusPanel/RenderUserSubscriptionMaybe/RenderUserSubscriptionMaybe';
import { subscriptionPlanSelector } from '../../../../ducks/stripe.duck';
import {
  deleteItemFromFavorites,
  favoritesSelector,
} from '../../../FavoritesPage/FavoritesPage.duck';
import { useConfiguration } from '../../../../context/configurationContext';
import { createSlug } from '../../../../util/urlHelpers';
import { setListingSessionUrl } from '../../../../util/helperFunction';

const SectionBrandHero = (props, {}) => {
  const {
    publicData,
    isOwnListing,
    listing,
    showInlineBanners,
    currentAuthor,
    authorId,
    onSubmitInquiry,
    setOpenSocialSharingModal = () => {},
  } = useListingPageContext();

  const { isMobileLayout, intl, viewport } = props;
  const { title = '', description = '' } = listing?.attributes || {};

  const { state, city, zipCode, customLinks } = publicData || {};

  const currentIndustry = brandIndustriesOptions.find(
    industry => industry.value === publicData?.pub_primary_industry
  );

  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser = useSelector(userSelector);
  const { subscriptionPrices } = useSelector(subscriptionPlanSelector);
  const { removedItemId } = useSelector(favoritesSelector);
  const [activeFavoriteId, setActiveFavoriteId] = useState(null);
  const onRedirectPage = (page, pageParams = {}) => {
    history.push(createResourceLocatorString(page, routeConfiguration, pageParams, {}));
  };

  const [activeTab, setActiveTab] = useState(SectionBrandHeroTabs.HOME);
  const sectionRefs = useRef({});

  const scrollToSection = tab => {
    const sectionRef = sectionRefs.current[tab];
    if (sectionRef) {
      sectionRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleTabClick = tab => {
    setActiveTab(tab);
    scrollToSection(tab);
  };

  const renderInfo = (label, value, icon = null) => (
    <p>
      <span className={css.serviceLabel}>
        <Icons name={icon || formatStringtoCamelCase(label)} /> {label ? `${label}: ` : ''}
      </span>
      <span>{value || 'N/A'}</span>
    </p>
  );
  const brandPremierMaybe = Boolean(
    getUserSubscription(listing?.author) === SUBSCRIPTION_TYPES.BRAND_PREMIER
  );

  const profileImageURL = getFirstListingImageUrl(listing);

  const sharedProps = {
    isMobileLayout,
    intl,
    viewport,
    profileImageIcon: profileImageURL,
  };

  const tabs = brandPremierMaybe
    ? Object.values(SectionBrandHeroTabs)
    : [SectionBrandHeroTabs.HOME];
  const authorDisplayName = getDisplayName(currentAuthor);
  const listingId = listing?.id?.uuid;
  const currentUserSubscription = getSubscriptionForListing(currentUser, listingId);
  const isCurrentUserSubscriptionActive =
    isArrayLength(currentUserSubscription) &&
    currentUserSubscription?.some(sub => sub.status === SubscriptionStatus.ACTIVE);

  const shouldShowBrandPremierBanner =
    !isOwnListing &&
    (!isAuthenticated || !isCurrentUserSubscriptionActive) &&
    activeTab !== SectionBrandHeroTabs.HOME;
  const lookupKeys = getUserSubscriptionLookupkeys(currentAuthor);
  const currentUserId = currentUser?.id?.uuid;
  const currentUserType = getUserType(currentUser);
  const isCurrentUserTalent = currentUserType === UserType.TALENT;
  const favorites = retrieveUserFavoritesList(currentUser) || [];
  const openFavoritesModal = id => setActiveFavoriteId(id);
  const favoritesList = favorites || [];
  const isAlreadyAddedToFavoriteslist =
    isArrayLength(favoritesList) &&
    favoritesList.some(category => category.ids?.includes(listingId));
  const firstImage = isArrayLength(listing?.images) ? listing.images[0] : null;

  const canViewCouponOffer = isAuthenticated || isOwnListing;
  const canViewBrandContent = (isAuthenticated && isCurrentUserTalent) || isOwnListing;

  const componentsToRender = [
    ...(canViewCouponOffer ? [RenderCouponOffer] : []),
    ...(canViewBrandContent ? [RenderJobPost, RenderCampaigns, RenderPerks] : []),
  ];
  const slug = createSlug(title);

  const navigateToLogin = () => {
    history.push(createResourceLocatorString('LoginPage', routeConfiguration, {}, {}));
    setListingSessionUrl(
      createResourceLocatorString('ListingPage', routeConfiguration, { slug, id: listingId }, {})
    );
  };
  const handleFavoritesList = useCallback(
    async e => {
      e.preventDefault();
      e.stopPropagation();

      try {
        if (!isAuthenticated) {
          navigateToLogin();
        }
        if (isOwnListing) {
          history.push(createResourceLocatorString('FavoritesPage', routeConfiguration, {}));
          return;
        }

        if (isAlreadyAddedToFavoriteslist) {
          await dispatch(
            deleteItemFromFavorites({
              isAuthenticated,
              id: listingId,
              favorites,
              config,
              userId: currentUserId,
            })
          );
        } else {
          openFavoritesModal(listingId);
        }
      } catch (error) {
        console.error('Error while updating favorites:', error);
      }
    },
    [
      isOwnListing,
      isAlreadyAddedToFavoriteslist,
      history,
      routeConfiguration,
      dispatch,
      listingId,
      favorites,
      config,
      currentUserId,
      openFavoritesModal,
    ]
  );
  const handleContactUserClick = e => {
    e.preventDefault();
    onSubmitInquiry();
  };
  const contactActions = [
    {
      key: 'favorite',
      icon: 'star',
      labelId: 'SectionHero.favorite',
      onClick: handleFavoritesList,
      className: isAlreadyAddedToFavoriteslist ? css.favorited : undefined,
      ariaLabel: isAlreadyAddedToFavoriteslist ? 'Remove from favorites' : 'Add to favorites',
      showSpinner: removedItemId === listingId,
    },
    { icon: 'messageBox', label: 'Chat', onClick: isOwnListing ? null : handleContactUserClick },
    { icon: 'share', label: 'Share', onClick: () => setOpenSocialSharingModal(true) },
  ];
  return (
    <div className={css.heroWrapper}>
      <div className={css.heroContainer}>
        <div className={css.heroBg}>
          <div className={css.contentWidth}>
            {showInlineBanners && isMobileLayout ? (
              <BrandWhiteLabelBanner listing={listing} />
            ) : null}
            <div className={css.heroContent}>
              <div className={css.heroContentHead}>
                <div className={css.heroContentHeadLeft}>
                  {isArrayLength(listing?.images) &&
                    listing?.images.slice(0, 1)?.map((img, index) => {
                      const imgUrl = img?.attributes?.variants['square-small2x']?.url;
                      return (
                        <div className={css.schoolImg} key={index}>
                          <img
                            src={imgUrl}
                            alt={`Featured ${index + 1}`}
                            className={css.featuredImage}
                          />
                        </div>
                      );
                    })}
                  <Heading as="h1" rootClassName={css.heading}>
                    {publicData?.organizationName}
                  </Heading>
                  <div className={css.verifySec}>
                    <Icons name="verify" /> <FormattedMessage id="BrandListingCard.verified" />
                    {brandPremierMaybe
                      ? SUBSCRIPTION_TYPES.BRAND_PREMIER
                      : SUBSCRIPTION_TYPES.BRAND_STANDARD}
                  </div>
                </div>
                <div className={css.heroContentHeadRight}>
                  <div className={css.bookSessionSec}>
                    <div className={css.contactSec}>
                      {contactActions.map(
                        ({
                          key,
                          icon,
                          label,
                          labelId,
                          onClick,
                          className,
                          ariaLabel,
                          showSpinner,
                        }) => (
                          <button
                            key={key || icon}
                            type="button"
                            className={className}
                            onClick={onClick}
                            aria-label={ariaLabel || label}
                          >
                            {showSpinner ? <IconSpinner /> : <Icons name={icon} />}
                            {labelId ? <FormattedMessage id={labelId} /> : label}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                  <div className={css.schoolInfo}>
                    <div className={css.address}>
                      {renderInfo('Industry', currentIndustry?.label, 'industryIcon')}
                      {renderInfo('City', city, 'cityIcon')}
                      {renderInfo('State', state, 'location')}
                      {renderInfo('Zip Code', zipCode, 'zipCode')}
                    </div>
                  </div>
                  {isArrayLength(customLinks) && brandPremierMaybe && (
                    <div className={css.keywordSec}>
                      <h2>
                        <FormattedMessage id="SectionHero.customLinks" />
                      </h2>
                      <div className={css.keywords}>
                        {customLinks.map((link, index) => (
                          <span key={index}>
                            <strong>
                              <a href={link?.url} target="_blank" rel="noopener noreferrer">
                                {link?.name}
                              </a>
                            </strong>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={css.heroContentImg}>
              {showInlineBanners && !isMobileLayout ? (
                <BrandWhiteLabelBanner listing={listing} />
              ) : null}
              {showInlineBanners && isOwnListing && !brandPremierMaybe ? (
                <BrandsInlineBanner intl={intl} onRedirectPage={onRedirectPage} />
              ) : null}
              <div className={css.contentHeroTabs}>
                <div className={css.tabs}>
                  {tabs.map((tab, index) => (
                    <span
                      key={index}
                      className={tab === activeTab ? css.active : ''}
                      onClick={() => handleTabClick(tab)}
                    >
                      {tab}
                    </span>
                  ))}
                </div>

                {isOwnListing && (
                  <div className={css.editSec}>
                    <SecondaryButton
                      type="button"
                      onClick={() =>
                        onRedirectPage('EditListingPage', {
                          ...params,
                          type: EditListingParamsType.EDIT,
                          tab: EditListingTab.DETAILS,
                        })
                      }
                    >
                      <Icons name="pen" /> <FormattedMessage id="SectionHero.edit" />
                    </SecondaryButton>
                  </div>
                )}
              </div>
              {/* {shouldShowBrandPremierBanner && (
                <div className={css.brandPremierBanner}>
                  <FormattedMessage
                    id="SectionHero.talentBanner.message"
                    values={{ talentName: <strong>{title}</strong> }}
                  />
                </div>
              )} */}
              {activeTab === SectionBrandHeroTabs.HOME && (
                <>
                  <div className={css.howItWorks}>
                    <div className={css.descriptionContainer}>{description}</div>
                  </div>
                  {componentsToRender.map((Component, index) => (
                    <Component key={index} {...sharedProps} />
                  ))}
                </>
              )}
              {{
                [SectionBrandHeroTabs.BRAND_LINKS]: (
                  <BrandCustomLinks publicData={publicData} isMobileLayout={isMobileLayout} />
                ),
                [SectionBrandHeroTabs.SOCIAL_MEDIA_FEEDS]: (
                  <SocialMediaFeeds publicData={publicData} isMobileLayout={isMobileLayout} />
                ),
                [SectionBrandHeroTabs.POSTED_CONTENT]: (
                  <PostedContent publicData={publicData} isMobileLayout={isMobileLayout} />
                ),
                [SectionBrandHeroTabs.CAMPAIGN_SHOWCASE]: (
                  <CampaignShowCase publicData={publicData} isMobileLayout={isMobileLayout} />
                ),
              }[activeTab] || null}

              {activeTab === SectionBrandHeroTabs.SUBSCRIPTION && (
                <RenderUserSubscriptionMaybe
                  lookupKeys={lookupKeys}
                  subscriptions={subscriptionPrices}
                  isListingPage={true}
                  isOwnListing={isOwnListing}
                  sectionRef={el => (sectionRefs.current[SectionBrandHeroTabs.SUBSCRIPTION] = el)}
                  authorId={authorId}
                  listingId={listingId}
                  currentUserSubscription={currentUserSubscription}
                />
              )}
              <FavoritesModal
                isOpen={!!activeFavoriteId}
                onClose={() => setActiveFavoriteId(null)}
                firstImage={firstImage}
                currentUserId={currentUserId}
                activeFavoriteId={activeFavoriteId}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionBrandHero;
